import { useState } from 'react'

import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { DateValueI } from '@/components/ui'
import { Role } from '@/features/managed-accounts'
import { useFacilityNamesQuery, useUsersQuery } from '@/graphql/generated/hooks'
import { FacilityType, GuardTourStatus } from '@/graphql/generated/schemas'
import { MultiFilter } from '@/redux/types/filters'
import {
  selectSubmittedToursFacilityIdsFilter,
  selectSubmittedToursGuardIdsFilter,
  selectSubmittedToursStatusFilter,
  selectSubmittedToursSubmittedAtRangeFilter,
  setSubmittedToursFacilityIdsFilter,
  setSubmittedToursGuardIdsFilter,
  setSubmittedToursStatusFilter,
  setSubmittedToursSubmittedAtRangeFilter,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { FilterOptionI } from '@/utils/filterDrawers'
import {
  getFacilityOptions,
  getUserOptions,
} from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

interface SubmittedToursFilterDrawerIProps {
  onClose: () => void
}

interface SubmittedToursFiltersFormInputsI {
  submittedAt?: FilterOptionI<number | DateValueI>
  facilityName?: MultiFilter
  guardName?: MultiFilter
  status?: MultiFilter
}

export const useSubmittedToursFilterDrawer = ({
  onClose,
}: SubmittedToursFilterDrawerIProps) => {
  const toTitleCase = (str: string) =>
    str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())

  const guardTourStatusOptions = Object.values(GuardTourStatus).map(
    (value) => ({
      value: value,
      label: toTitleCase(value),
    })
  )

  const dispatch = useDispatch()

  const [facilitySearchValue, setFacilitySearchValue] = useState('')
  const [guardSearchValue, setGuardSearchValue] = useState('')

  const [debouncedfacilitySearchValue] = useDebounce(
    facilitySearchValue,
    SEARCH_DEBOUNCE_TIME
  )

  const [debouncedGuardSearchValue] = useDebounce(
    guardSearchValue,
    SEARCH_DEBOUNCE_TIME
  )

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          type: FacilityType.Normal,
          ...(debouncedfacilitySearchValue?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilitySearchValue }
            : {}),
        },
      },
    })

  const guardNameLikeFilter = JSON.parse(
    JSON.stringify({
      OR: [
        {
          firstNameLike: debouncedGuardSearchValue,
        },
        { lastNameLike: debouncedGuardSearchValue },
        { usernameLike: debouncedGuardSearchValue },
        { fullNameLike: debouncedGuardSearchValue },
      ],
    })
  )

  const { data: guardsData, loading: isGuardDataLoading } = useUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        roleName: Role.GUARD,
        ...(debouncedGuardSearchValue?.length >= MIN_CHAR_COUNT
          ? guardNameLikeFilter
          : {}),
      },
    },
  })
  const facilityIdsFilterValue = useSelector(
    selectSubmittedToursFacilityIdsFilter
  )
  const guardIdsFilterValue = useSelector(selectSubmittedToursGuardIdsFilter)
  const submittedAtFilterValue = useSelector(
    selectSubmittedToursSubmittedAtRangeFilter
  )
  const statusFilterValue = useSelector(selectSubmittedToursStatusFilter)

  const facilityOptions = getFacilityOptions(facilitiesData)
  const guardOptions = getUserOptions(guardsData)

  const onSubmit: SubmitHandler<SubmittedToursFiltersFormInputsI> = async (
    values
  ) => {
    const selectedFacilityIds = values?.facilityName
    const selectedGuardIds = values?.guardName
    const selectedSubmittedAtRange = values?.submittedAt
    const selectedstatus = values?.status

    dispatch(
      setSubmittedToursFacilityIdsFilter(
        selectedFacilityIds?.length ? selectedFacilityIds : null
      )
    )
    dispatch(
      setSubmittedToursGuardIdsFilter(
        selectedGuardIds?.length ? selectedGuardIds : null
      )
    )
    dispatch(
      setSubmittedToursSubmittedAtRangeFilter(selectedSubmittedAtRange || null)
    )
    dispatch(setSubmittedToursStatusFilter(selectedstatus || null))

    onClose()
  }

  const { control, handleSubmit, watch, setValue } =
    useForm<SubmittedToursFiltersFormInputsI>({
      reValidateMode: 'onChange',
    })

  const submittedAtFilterCount = watch('submittedAt') == null ? null : 1

  const onClearFilters = () => {
    setValue('facilityName', null)
    setValue('guardName', null)
    setValue('status', null)
    setValue('submittedAt', null)

    trackEvent(`${BUTTON_PRESS} Guard Tours Filter Drawer - Clear Filters`)
  }

  return {
    guardTourStatusOptions,
    isFacilitiesLoading,
    isGuardDataLoading,
    onSubmit,
    onClearFilters,
    submittedAtFilterCount,
    control,
    handleSubmit,
    facilityIdsFilterValue,
    guardIdsFilterValue,
    submittedAtFilterValue,
    statusFilterValue,
    facilityOptions,
    guardOptions,
    facilitySearchValue,
    setFacilitySearchValue,
    guardSearchValue,
    setGuardSearchValue,
    watch,
    setValue,
  }
}
